import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { PageTitle, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Wallet, Eye, ArrowRightCircle, Plus, Replace, AlertTriangle,
} from "lucide-react";
import { formatRupiah } from "@/lib/format";
import { toast } from "sonner";

export default function BudgetScenarios() {
  const { user } = useAuth();
  const { data, reloadAll } = useData();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);        // { scenario }
  const [confirm, setConfirm] = useState(null);      // { scenario, existingCount }
  const [applying, setApplying] = useState(false);

  // Items already saved in the Pernikahan (Nikah) tab — used to decide
  // whether the user should see the Replace / Append confirmation.
  const existingPernikahan = (data.budget || []).filter((b) => b.agenda === "Pernikahan");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/public/budget-scenarios"); setScenarios(data); }
      catch { toast.error("Gagal memuat skenario"); }
      finally { setLoading(false); }
    })();
  }, []);

  // Entry point used by both card and detail dialog.
  const startApply = (scenario) => {
    if (!user?.pasangan_id && user?.role !== "sysadmin" && user?.role !== "Admin") {
      toast.error("Pasangan belum dipilih"); return;
    }
    if (existingPernikahan.length > 0) {
      // Ask user to pick Replace vs Append.
      setConfirm({ scenario, existingCount: existingPernikahan.length });
    } else {
      // Straight apply as append (empty tab).
      doApply(scenario, false);
    }
  };

  const doApply = async (scenario, replace) => {
    setApplying(true);
    try {
      const { data: res } = await api.post("/data/budget/apply-scenario", {
        scenario_id: scenario.id, replace,
      });
      toast.success(
        replace
          ? `Tab Nikah diganti dengan skenario "${scenario.nama}" (${res.added} kategori).`
          : `${res.added} kategori dari "${scenario.nama}" ditambahkan ke tab Nikah.`
      );
      setDetail(null);
      setConfirm(null);
      await reloadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menerapkan skenario");
    } finally { setApplying(false); }
  };

  return (
    <div>
      <PageTitle title="Skenario Budget" subtitle="Referensi budget pernikahan siap pakai" icon={Wallet} />

      {loading ? <p className="text-sm text-muted-foreground">Memuat…</p> :
       scenarios.length === 0 ? <EmptyState text="Belum ada skenario budget tersedia." /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {scenarios.map((s) => (
            <Card key={s.id} data-testid={`scenario-card-${s.id}`} className="flex flex-col p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-primary">Bundle</p>
                  <h3 className="font-heading text-xl font-semibold">{s.nama}</h3>
                </div>
                <Badge>{s.items?.length || 0} kategori</Badge>
              </div>
              {s.deskripsi && <p className="mt-2 text-sm text-muted-foreground">{s.deskripsi}</p>}
              <p className="mt-4 text-2xl font-bold text-primary">{formatRupiah(s.total || 0)}</p>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Total estimasi</p>
              <div className="mt-auto flex gap-2 pt-4">
                <Button data-testid={`scenario-detail-${s.id}`} onClick={() => setDetail(s)} variant="outline" className="flex-1 gap-1.5">
                  <Eye className="h-3.5 w-3.5" /> Detail
                </Button>
                <Button data-testid={`scenario-apply-${s.id}`} onClick={() => startApply(s)} className="flex-1 gap-1.5">
                  <ArrowRightCircle className="h-3.5 w-3.5" /> Terapkan
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Detail view */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="scenario-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{detail?.nama}</DialogTitle>
            <DialogDescription>{detail?.deskripsi || "Rincian budget per kategori — akan masuk ke tab Nikah pada menu Budget"}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto">
            <div className="space-y-2">
              {(detail?.items || []).map((it, i) => (
                <div key={i} className="flex items-start justify-between rounded-lg border border-border p-3 text-sm">
                  <div>
                    <p className="font-semibold">{it.kategori}</p>
                    {it.catatan && <p className="text-xs text-muted-foreground">{it.catatan}</p>}
                  </div>
                  <p className="font-mono tabular-nums">{formatRupiah(it.nominal)}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between rounded-lg bg-primary/10 p-4">
              <span className="font-semibold">Total</span>
              <span className="font-mono text-lg font-bold text-primary">{formatRupiah(detail?.total || 0)}</span>
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={() => setDetail(null)} className="flex-1">Tutup</Button>
            <Button data-testid="scenario-apply-from-detail" disabled={applying} onClick={() => startApply(detail)} className="flex-1 gap-1.5">
              <ArrowRightCircle className="h-3.5 w-3.5" /> Terapkan ke Budget
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace vs Append confirmation */}
      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent className="sm:max-w-md" data-testid="scenario-confirm-dialog">
          <DialogHeader>
            <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-amber-500/15">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            </div>
            <DialogTitle className="font-heading text-xl">Sudah ada budget di tab Nikah</DialogTitle>
            <DialogDescription>
              Tab Nikah sudah memiliki <b>{confirm?.existingCount}</b> item budget.
              Terapkan skenario <b>"{confirm?.scenario?.nama}"</b> sebagai:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-1">
            <button
              type="button"
              data-testid="scenario-choose-append"
              disabled={applying}
              onClick={() => doApply(confirm.scenario, false)}
              className="w-full rounded-lg border border-border p-4 text-left transition-all hover:border-primary hover:bg-primary/5 disabled:opacity-60"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10">
                  <Plus className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">Tambahkan di bawah</p>
                  <p className="text-xs text-muted-foreground">Item lama tetap ada, item skenario ditambahkan.</p>
                </div>
              </div>
            </button>
            <button
              type="button"
              data-testid="scenario-choose-replace"
              disabled={applying}
              onClick={() => doApply(confirm.scenario, true)}
              className="w-full rounded-lg border border-border p-4 text-left transition-all hover:border-destructive hover:bg-destructive/5 disabled:opacity-60"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-destructive/10">
                  <Replace className="h-4 w-4 text-destructive" />
                </div>
                <div>
                  <p className="font-semibold">Ganti semua</p>
                  <p className="text-xs text-muted-foreground">
                    Hapus {confirm?.existingCount} item di tab Nikah, ganti dengan skenario ini.
                    Item tab Lamaran tidak berubah.
                  </p>
                </div>
              </div>
            </button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)} disabled={applying} data-testid="scenario-confirm-cancel">Batal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
